// =============================================================================
// PPIDesistencia — a desistência do candidato, compartilhada pelas TRÊS abas
// =============================================================================
// Registrar que alguém saiu do processo é a MESMA ação na Triagem, na Aplicação e
// na LIVE — mesma coluna no banco, mesmo motivo obrigatório, mesma marca cinza.
// Este arquivo existe para que ela seja escrita uma vez só: em 2026-08-05 dois
// formulários do Treinamento que eram cópia um do outro custaram meses com uma
// trava de perfil faltando em um dos lados. Aqui a lição é a mesma, um módulo antes.
//
// ⚠️ A marca é CINZA, nunca vermelha. Desistir não é reprovar — o vermelho
// equipararia visualmente as duas coisas, que é exatamente a confusão que a
// desistência veio desfazer (antes dela, para tirar alguém da tela era preciso
// REPROVÁ-LO).

import React, { useState } from 'react';
import { UserMinus, RotateCcw } from 'lucide-react';
import { CORES, BadgeStatus, Botao, estilos } from './ppiUI';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

// As rotas são do CANDIDATO (não da LIVE, onde nasceram em 2026-08-04): elas saíram
// de /chao em 2026-08-06 justamente porque a Triagem também precisa delas.
const API_CANDIDATO = '/api/treinamento/ppi/candidato';

export const DESISTENTE = 'Desistente';

// Aceita o STATUS cru. As telas trazem o candidato em formatos diferentes
// (`c.STATUS` na Triagem/LIVE, `c.status` no ranking), então quem chama passa o
// campo — assim não há um "adivinhador de formato" para manter.
export const ehDesistente = (status) => status === DESISTENTE;

/** POST — registra. Devolve a mensagem de erro do servidor, se houver. */
export async function registrarDesistencia(idCandidato, motivo) {
    const r = await fetch(`${API_CANDIDATO}/${idCandidato}/desistir`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ motivo }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || 'Erro ao registrar a desistência.');
    return data;
}

/** DELETE — desfaz (registro por engano). O motivo registrado é apagado junto. */
export async function desfazerDesistencia(idCandidato) {
    const r = await fetch(`${API_CANDIDATO}/${idCandidato}/desistir`, {
        method: 'DELETE', headers: authHeaders(),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || 'Erro ao desfazer a desistência.');
    return data;
}

/** Marca de "saiu do processo" — badge cinza + o motivo logo abaixo, se houver. */
export function MarcaDesistiu({ motivo, largura = '260px' }) {
    return (
        <div>
            <BadgeStatus tom="neutro" icone={UserMinus}>Desistiu</BadgeStatus>
            {motivo && (
                <div style={{ fontSize: '12px', color: CORES.textoFraco, marginTop: '4px', maxWidth: largura }}>
                    {motivo}
                </div>
            )}
        </div>
    );
}

/** O link discreto "desistiu" que abre o modal. Ação rara — não compete com a principal. */
export function BotaoDesistir({ onClick, titulo = 'Registrar que o candidato desistiu do processo' }) {
    return (
        <button type="button" onClick={onClick} title={titulo}
            style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: CORES.textoFraco, fontSize: '12px', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px',
            }}>
            <UserMinus size={13} /> desistiu
        </button>
    );
}

/**
 * Desfazer. Confirma porque devolve a pessoa à fila de trabalho e APAGA o motivo
 * registrado — o candidato volta para a etapa de onde saiu (o servidor sabe qual).
 */
export function BotaoDesfazerDesistencia({ idCandidato, nome, onPronto, onErro, compacto = false }) {
    const [salvando, setSalvando] = useState(false);

    const clicar = async () => {
        if (!window.confirm(
            `Desfazer a desistência de ${nome}? Ele volta para a etapa em que estava e o motivo registrado é apagado.`
        )) return;
        setSalvando(true);
        try {
            await desfazerDesistencia(idCandidato);
            onPronto();
        } catch (e) { onErro(e.message); }
        finally { setSalvando(false); }
    };

    return (
        <Botao variant="secundario" icone={RotateCcw} onClick={clicar} disabled={salvando}>
            {compacto ? 'Desfazer' : 'Desfazer desistência'}
        </Botao>
    );
}

/**
 * O modal do registro. O MOTIVO É OBRIGATÓRIO (o backend também exige): sem ele,
 * daqui a três meses ninguém sabe se a pessoa saiu da empresa, recusou o cargo ou
 * pediu para tentar na próxima edição — e as três levam a decisões diferentes.
 */
export function ModalDesistencia({ idCandidato, nome, detalhe, onFechar, onPronto }) {
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    const confirmar = async () => {
        if (!motivo.trim()) { setErro('Informe o motivo da desistência.'); return; }
        setSalvando(true); setErro('');
        try {
            await registrarDesistencia(idCandidato, motivo.trim());
            onPronto();
        } catch (e) { setErro(e.message); }
        finally { setSalvando(false); }
    };

    return (
        <div onClick={onFechar}
            style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 50,
            }}>
            <div onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: '#fff', borderRadius: '14px', padding: '24px',
                    width: '100%', maxWidth: '460px', boxShadow: '0 20px 50px rgba(15,23,42,0.25)',
                }}>
                <h3 style={{ margin: '0 0 6px 0', fontSize: '17px', fontWeight: 800, color: CORES.texto, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserMinus size={18} color={CORES.textoSec} /> Registrar desistência
                </h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: CORES.textoSec }}>
                    <strong>{nome}</strong>{detalhe ? ` — ${detalhe}` : ''}
                </p>

                <label style={estilos.rotuloFiltro}>Motivo *</label>
                <textarea
                    value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} autoFocus
                    placeholder="Ex.: pediu para participar na próxima edição; recusou a mudança de loja; saiu da empresa"
                    style={{ ...estilos.input, resize: 'vertical', fontFamily: 'inherit' }} />

                <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '10px 0 0 0' }}>
                    O candidato continua na lista, marcado como desistente, e sai das filas de trabalho
                    (triagem, chamada da sala e avaliação). Dá para desfazer se o registro tiver sido por engano.
                </p>

                {erro && <p style={{ color: '#be123c', fontSize: '13px', margin: '10px 0 0 0' }}>{erro}</p>}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <Botao variant="secundario" onClick={onFechar} disabled={salvando}>Cancelar</Botao>
                    <Botao variant="primario" icone={UserMinus} onClick={confirmar} disabled={salvando}>
                        {salvando ? 'Registrando…' : 'Registrar desistência'}
                    </Botao>
                </div>
            </div>
        </div>
    );
}
